import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';
import upperFirst from 'lodash/upperFirst';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Icon } from 'semantic-ui-react';
import { usePopup } from '../../lib/popup';

import DroppableTypes from '../../constants/DroppableTypes';
import CardContainer from '../../containers/CardContainer';
import CardAdd from './CardAdd';
import NameEdit from './NameEdit';
import ActionsStep from './ActionsStep';
import { ReactComponent as PlusMathIcon } from '../../assets/images/plus-math-icon.svg';

import styles from './List.module.scss';
import globalStyles from '../../styles.module.scss';

// FAMILY-EXT: матрица Эйзенхауэра — раскладка квадрантов.
// 2×2 grid: Q1 top-left, Q2 top-right, Q3 bottom-left, Q4 bottom-right.
// Линейный режим (slева→направо по возрастанию значимости): Q4, Q3, Q2, Q1.
const QUADRANTS_GRID = ['q1', 'q2', 'q3', 'q4'];
const QUADRANTS_LINEAR = ['q4', 'q3', 'q2', 'q1'];

const List = React.memo(
  ({
    id,
    index,
    name,
    color,
    viewMode,
    isActive,
    isPersisted,
    cardIds,
    cardIdsByQuadrant,
    canEdit,
    onUpdate,
    onDelete,
    onSort,
    onCardCreate,
  }) => {
    const [t] = useTranslation();
    // FAMILY-EXT: позиция формы добавления карточки — null | 'top' | 'bottom'.
    // 'top' = плюсик в шапке списка (карточка добавляется в начало),
    // 'bottom' = кнопка под списком (карточка в конец, дефолтное поведение).
    const [addCardOpenedAt, setAddCardOpenedAt] = useState(null);
    const isAddCardOpenedTop = addCardOpenedAt === 'top';
    const isAddCardOpenedBottom = addCardOpenedAt === 'bottom';
    // FAMILY-EXT: матрица Эйзенхауэра — null | 'q1'..'q4' | 'unassigned'
    const [addCardOpenedQuadrant, setAddCardOpenedQuadrant] = useState(null);
    // FAMILY-EXT: матрица Эйзенхауэра — раскладка: 'grid' (2×2) | 'linear' (4 в ряд)
    // Персистится в localStorage по ключу family-matrix-layout:<listId>,
    // чтобы выбор пользователя восстанавливался после F5 / возврата на доску.
    const matrixLayoutKey = `family-matrix-layout:${id}`;
    const [matrixLayout, setMatrixLayoutRaw] = useState(() => {
      try {
        const saved = window.localStorage.getItem(matrixLayoutKey);
        if (saved === 'grid' || saved === 'linear') return saved;
      } catch (e) {
        // ignore — localStorage может быть выключен
      }
      return 'grid';
    });
    const setMatrixLayout = useCallback(
      (next) => {
        setMatrixLayoutRaw(next);
        try {
          window.localStorage.setItem(matrixLayoutKey, next);
        } catch (e) {
          // ignore
        }
      },
      [matrixLayoutKey],
    );

    const nameEdit = useRef(null);
    const cardsWrapper = useRef(null);

    const isMatrix = viewMode === 'eisenhower';

    const handleHeaderClick = useCallback(() => {
      if (isPersisted && canEdit) {
        nameEdit.current.open();
      }
    }, [isPersisted, canEdit]);

    const handleNameUpdate = useCallback(
      (newName) => {
        onUpdate({
          name: newName,
        });
      },
      [onUpdate],
    );

    const handleColorEdit = useCallback(
      (newColor) => {
        onUpdate({
          color: newColor,
        });
      },
      [onUpdate],
    );

    const handleAddCardClick = useCallback(() => {
      setAddCardOpenedAt('bottom');
    }, []);

    // FAMILY-EXT: плюсик в шапке списка — открывает форму сверху,
    // карточка добавляется в начало списка (addToTop=true).
    const handleAddCardTopClick = useCallback((e) => {
      e.stopPropagation();
      setAddCardOpenedAt('top');
    }, []);

    const handleAddCardClose = useCallback(() => {
      setAddCardOpenedAt(null);
    }, []);

    const handleNameEdit = useCallback(() => {
      nameEdit.current.open();
    }, []);

    const handleCardAdd = useCallback(() => {
      setAddCardOpenedAt('bottom');
    }, []);

    // FAMILY-EXT: обёртка onCreate для верхней формы — добавляет флаг addToTop,
    // чтобы saga посчитала position перед первой карточкой.
    const handleCardCreateTop = useCallback(
      (data, autoOpen) => {
        onCardCreate(data, autoOpen, true);
      },
      [onCardCreate],
    );

    // FAMILY-EXT: матрица Эйзенхауэра — toggle режима из ActionsStep
    const handleToggleMatrix = useCallback(() => {
      onUpdate({ viewMode: isMatrix ? 'default' : 'eisenhower' });
    }, [onUpdate, isMatrix]);

    // FAMILY-EXT: фильтр "Только активные" — toggle отметки активности
    const handleToggleActive = useCallback(() => {
      onUpdate({ isActive: !isActive });
    }, [onUpdate, isActive]);

    // FAMILY-EXT: матрица Эйзенхауэра — кнопка "Свернуть" в шапке.
    // Backend при viewMode='default' автоматически пересортирует cards
    // по порядку Q1→Q2→Q3→Q4→null (helpers/lists/update-one.js).
    // stopPropagation — иначе клик прорастает в дядишний div header'а
    // и открывает NameEdit (тот же баг был бы и с pencil-кнопкой, но
    // semantic-ui Popup перехватывает клик первым).
    const handleCollapseMatrix = useCallback(
      (e) => {
        e.stopPropagation();
        onUpdate({ viewMode: 'default' });
      },
      [onUpdate],
    );

    // FAMILY-EXT: матрица Эйзенхауэра — создание карточки в конкретном
    // квадранте. Прокидываем eisenhowerQuadrant в data, backend сам
    // запишет поле при INSERT (controllers/cards/create.js).
    const makeQuadrantCardCreate = useCallback(
      (quadrantKey) => (data, autoOpen) => {
        const normalizedQuadrant = quadrantKey === 'unassigned' ? null : quadrantKey;
        onCardCreate({ ...data, eisenhowerQuadrant: normalizedQuadrant }, autoOpen);
      },
      [onCardCreate],
    );

    const handleQuadrantCardAddClose = useCallback(() => {
      setAddCardOpenedQuadrant(null);
    }, []);

    useEffect(() => {
      // FAMILY-EXT: автоскролл вниз — только при открытии нижней формы.
      // Для верхней формы наоборот скроллим к началу, чтобы видно поле ввода.
      if (isAddCardOpenedBottom) {
        cardsWrapper.current.scrollTop = cardsWrapper.current.scrollHeight;
      } else if (isAddCardOpenedTop) {
        cardsWrapper.current.scrollTop = 0;
      }
    }, [cardIds, isAddCardOpenedBottom, isAddCardOpenedTop]);

    const ActionsPopup = usePopup(ActionsStep);

    const renderQuadrant = (quadrantKey) => {
      const ids = cardIdsByQuadrant[quadrantKey] || [];
      const isQuadrantAddOpened = addCardOpenedQuadrant === quadrantKey;
      return (
        <Droppable
          key={quadrantKey}
          droppableId={`list:${id}:${quadrantKey}`}
          type={DroppableTypes.CARD}
          isDropDisabled={!isPersisted}
        >
          {({ innerRef, droppableProps, placeholder }) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div {...droppableProps} ref={innerRef} className={styles.quadrantInner}>
              <div className={styles.quadrantLabel}>
                {t(`common.quadrant${quadrantKey.toUpperCase()}`)}
              </div>
              <div className={styles.cards}>
                {ids.map((cardId, cardIndex) => (
                  <CardContainer key={cardId} id={cardId} index={cardIndex} />
                ))}
                {placeholder}
                {canEdit && (
                  <CardAdd
                    isOpened={isQuadrantAddOpened}
                    onCreate={makeQuadrantCardCreate(quadrantKey)}
                    onClose={handleQuadrantCardAddClose}
                  />
                )}
              </div>
              {canEdit && !isQuadrantAddOpened && (
                <button
                  type="button"
                  disabled={!isPersisted}
                  className={styles.quadrantAddCardButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAddCardOpenedQuadrant(quadrantKey);
                  }}
                >
                  <PlusMathIcon className={styles.addCardButtonIcon} />
                  <span className={styles.addCardButtonText}>
                    {ids.length > 0 ? t('action.addAnotherCard') : t('action.addCard')}
                  </span>
                </button>
              )}
            </div>
          )}
        </Droppable>
      );
    };

    const defaultCardsNode = (
      <Droppable
        droppableId={`list:${id}`}
        type={DroppableTypes.CARD}
        isDropDisabled={!isPersisted}
      >
        {({ innerRef, droppableProps, placeholder }) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <div {...droppableProps} ref={innerRef}>
            <div className={styles.cards}>
              {/* FAMILY-EXT: верхняя форма добавления (плюсик в шапке).
                  Рендерится перед списком карточек, чтобы визуально
                  быть сверху. */}
              {canEdit && isAddCardOpenedTop && (
                <CardAdd
                  isOpened
                  onCreate={handleCardCreateTop}
                  onClose={handleAddCardClose}
                />
              )}
              {cardIds.map((cardId, cardIndex) => (
                <CardContainer key={cardId} id={cardId} index={cardIndex} />
              ))}
              {placeholder}
              {canEdit && (
                <CardAdd
                  isOpened={isAddCardOpenedBottom}
                  onCreate={onCardCreate}
                  onClose={handleAddCardClose}
                />
              )}
            </div>
          </div>
        )}
      </Droppable>
    );

    // FAMILY-EXT: матрица Эйзенхауэра — 2×2 grid из 4 Droppable'ов +
    // отдельная строка "не разложенные" сверху, чтобы новые карточки
    // (eisenhowerQuadrant=null) было видно и легко перетащить.
    const matrixCardsNode = (
      <>
        {(cardIdsByQuadrant.unassigned || []).length > 0 && (
          <Droppable
            droppableId={`list:${id}:unassigned`}
            type={DroppableTypes.CARD}
            isDropDisabled={!isPersisted}
          >
            {({ innerRef, droppableProps, placeholder }) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <div {...droppableProps} ref={innerRef} className={styles.unassigned}>
                <div className={styles.quadrantLabel}>{t('common.unassigned')}</div>
                <div className={styles.unassignedCards}>
                  {cardIdsByQuadrant.unassigned.map((cardId, cardIndex) => (
                    <CardContainer key={cardId} id={cardId} index={cardIndex} />
                  ))}
                  {placeholder}
                </div>
              </div>
            )}
          </Droppable>
        )}
        <div
          className={classNames(
            styles.matrixGrid,
            matrixLayout === 'linear' && styles.matrixGridLinear,
          )}
        >
          {(matrixLayout === 'linear' ? QUADRANTS_LINEAR : QUADRANTS_GRID).map((q) =>
            renderQuadrant(q),
          )}
        </div>
      </>
    );

    return (
      <Draggable draggableId={`list:${id}`} index={index} isDragDisabled={!isPersisted || !canEdit}>
        {({ innerRef, draggableProps, dragHandleProps }) => (
          <div
            {...draggableProps} // eslint-disable-line react/jsx-props-no-spreading
            data-drag-scroller
            ref={innerRef}
            className={classNames(
              styles.innerWrapper,
              isMatrix && styles.innerWrapperMatrix,
              isMatrix && matrixLayout === 'linear' && styles.innerWrapperMatrixLinear,
            )}
          >
            <div
              className={classNames(
                styles.outerWrapper,
                isMatrix && styles.outerWrapperMatrix,
                isMatrix && matrixLayout === 'linear' && styles.outerWrapperMatrixLinear,
                // FAMILY-EXT: цветная полоска сверху, если у списка задан цвет
                color && globalStyles[`borderTop${upperFirst(camelCase(color))}`],
              )}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
                                           jsx-a11y/no-static-element-interactions */}
              <div
                {...dragHandleProps} // eslint-disable-line react/jsx-props-no-spreading
                className={classNames(
                  styles.header,
                  canEdit && styles.headerEditable,
                  isMatrix && styles.headerMatrix,
                )}
                onClick={handleHeaderClick}
              >
                <NameEdit ref={nameEdit} defaultValue={name} onUpdate={handleNameUpdate}>
                  <div className={styles.headerName}>
                    {/* FAMILY-EXT: иконка-кружок убрана, цвет показан полоской сверху */}
                    {name}
                  </div>
                </NameEdit>
                {/* FAMILY-EXT: матрица Эйзенхауэра — переключатель grid/linear
                    + кнопка "Свернуть". Видны только в матричном режиме.
                    stopPropagation, иначе клик пробрасывается в drag-handle
                    и открывает NameEdit. */}
                {isPersisted && canEdit && isMatrix && (
                  <>
                    <Button
                      className={classNames(
                        styles.layoutToggleButton,
                        matrixLayout === 'grid' && styles.layoutToggleButtonActive,
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMatrixLayout('grid');
                      }}
                      title={t('action.matrixLayoutGrid')}
                    >
                      <Icon fitted name="th" size="small" />
                    </Button>
                    <Button
                      className={classNames(
                        styles.layoutToggleButtonSecond,
                        matrixLayout === 'linear' && styles.layoutToggleButtonActive,
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMatrixLayout('linear');
                      }}
                      title={t('action.matrixLayoutLinear')}
                    >
                      <Icon fitted name="bars" size="small" />
                    </Button>
                    <Button
                      className={classNames(styles.collapseButton)}
                      onClick={handleCollapseMatrix}
                      title={t('action.collapseMatrix')}
                    >
                      <Icon fitted name="compress" size="small" />
                    </Button>
                  </>
                )}
                {/* FAMILY-EXT: плюсик справа от названия — добавить карточку
                    в начало списка. В матричном режиме скрыт, т.к. там add-card
                    рендерится отдельно в каждом квадранте. */}
                {isPersisted && canEdit && !isMatrix && (
                  <Button
                    className={classNames(styles.addCardTopButton, styles.target)}
                    onClick={handleAddCardTopClick}
                    title={t('action.addCard')}
                  >
                    <Icon fitted name="plus" size="small" />
                  </Button>
                )}
                {isPersisted && canEdit && (
                  <ActionsPopup
                    onNameEdit={handleNameEdit}
                    onCardAdd={handleCardAdd}
                    onDelete={onDelete}
                    onSort={onSort}
                    color={color}
                    onColorEdit={handleColorEdit}
                    isMatrix={isMatrix}
                    onToggleMatrix={handleToggleMatrix}
                    isActive={isActive}
                    onToggleActive={handleToggleActive}
                  >
                    <Button className={classNames(styles.headerButton, styles.target)}>
                      <Icon fitted name="pencil" size="small" />
                    </Button>
                  </ActionsPopup>
                )}
              </div>
              <div
                ref={cardsWrapper}
                className={classNames(
                  styles.cardsInnerWrapper,
                  isMatrix && styles.cardsInnerWrapperMatrix,
                )}
              >
                <div
                  className={classNames(
                    styles.cardsOuterWrapper,
                    isMatrix && styles.cardsOuterWrapperMatrix,
                  )}
                >
                  {isMatrix ? matrixCardsNode : defaultCardsNode}
                </div>
              </div>
              {!isMatrix && !isAddCardOpenedBottom && canEdit && (
                <button
                  type="button"
                  disabled={!isPersisted}
                  className={styles.addCardButton}
                  onClick={handleAddCardClick}
                >
                  <PlusMathIcon className={styles.addCardButtonIcon} />
                  <span className={styles.addCardButtonText}>
                    {cardIds.length > 0 ? t('action.addAnotherCard') : t('action.addCard')}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}
      </Draggable>
    );
  },
);

List.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
  viewMode: PropTypes.string,
  isActive: PropTypes.bool,
  isPersisted: PropTypes.bool.isRequired,
  cardIds: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  cardIdsByQuadrant: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  canEdit: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCardCreate: PropTypes.func.isRequired,
};

List.defaultProps = {
  color: undefined,
  viewMode: 'default',
  isActive: false,
  cardIdsByQuadrant: { q1: [], q2: [], q3: [], q4: [], unassigned: [] },
};

export default List;
